// §4.9's promise drawer, opened from `?promise=<id>` on the list (spec 03
// §4.9, D-463, D-464): the promise's facts and dates, the basis as a checklist
// with each assumption's last verdict, the verdict once judged, the timeline
// of its transitions and Withdraw for a holder of the code its status needs:
// `planning.promise.commit` for a quoted promise, `sales.order.re_promise` for
// a committed or at-risk one (D-464 ruling 2, J-3). On a phone it is a
// full-screen sheet.

import { useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { ErrorState, Skeleton } from "../../../../components/states";
import { Banner } from "../../../../components/toast";
import { formatTimestamp } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { usePlanningPermissions } from "../hooks/permissions";
import { usePlanningSettings, useProductIndex } from "../hooks/planning-queries";
import {
  PROMISE_REASON_MAX,
  usePromise,
  usePromiseRefresh,
  withdrawPromise,
  type CtpPromiseDetail,
} from "../hooks/promise-queries";
import { formatDate, useOffline } from "../lib/planning-lib";
import { canWithdrawPromise } from "../lib/promise-lib";
import { usePlanningFacility } from "../planning-layout";
import { refusalText } from "./ctp-dialog";
import {
  BaseQty,
  BasisChecklist,
  PromisePathChip,
  PromiseStatusChip,
  promiseProductText,
} from "./promise-bits";

export interface PromiseDrawerProps {
  readonly id: string;
  readonly onClose: () => void;
}

export function PromiseDrawer({ id, onClose }: PromiseDrawerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const detail = usePromise(id);
  const [pending, setPending] = useState(false);

  if (detail.error instanceof UnauthorizedError) {
    void navigate({ to: "/login" });
    return null;
  }

  const promise = detail.data;
  const heading =
    promise === undefined ? (
      t("planning.promises.drawer.label")
    ) : (
      <span className="planning-drawer-title">
        <span data-testid="planning-promise-drawer-no">{promise.promise_no}</span>
        <PromiseStatusChip status={promise.status} />
      </span>
    );

  let body: ReactNode;
  if (detail.error !== null) {
    const gone =
      detail.error instanceof RequestError &&
      (detail.error.code === "PROMISE_NOT_FOUND" || detail.error.httpStatus === 404);
    body = gone ? (
      <p className="planning-note" data-testid="planning-promise-drawer-gone">
        {t("planning.promises.drawer.gone")}
      </p>
    ) : (
      <ErrorState
        title={t("planning.promises.drawer.loadError")}
        retryLabel={t("planning.common.retry")}
        onRetry={() => void detail.refetch()}
        requestId={requestIdOf(detail.error)}
        requestIdLabel={t("planning.common.requestId")}
      />
    );
  } else if (promise === undefined) {
    body = <DrawerSkeleton />;
  } else {
    body = <DrawerBody promise={promise} onPending={setPending} />;
  }

  return (
    <Modal
      label={promise === undefined ? t("planning.promises.drawer.label") : promise.promise_no}
      heading={heading}
      closeLabel={t("planning.common.close")}
      onClose={onClose}
      pending={pending}
      className="planning-drawer planning-order-sheet"
      testId="planning-promise-drawer"
    >
      {body}
    </Modal>
  );
}

function DrawerSkeleton() {
  const { t } = useTranslation();
  return (
    <div role="status" aria-label={t("planning.promises.drawer.loading")}>
      {(["facts", "basis", "timeline"] as const).map((s) => (
        <section
          key={s}
          className="planning-drawer-section"
          data-testid={`planning-promise-drawer-skeleton-${s}`}
        >
          <Skeleton width="30%" height="1.1rem" />
          <Skeleton height="0.9rem" />
          <Skeleton height="0.9rem" />
        </section>
      ))}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="planning-fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function DrawerBody({
  promise,
  onPending,
}: {
  promise: CtpPromiseDetail;
  onPending: (busy: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const { facilities } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const lang = i18n.language;
  const facility = facilities.find((f) => f.id === promise.facility_id);
  const zone = facility?.timezone;
  const products = useProductIndex([promise.product_id]);
  const product = products.byId.get(promise.product_id);
  const settings = usePlanningSettings(promise.facility_id);
  const withdrawable = canWithdrawPromise(
    promise,
    perms.has("planning.promise.commit"),
    perms.canRePromise,
  );
  const late =
    promise.current_projected_date !== null &&
    promise.current_projected_date > promise.promised_date;

  return (
    <div
      className="planning-order-body"
      data-promise-id={promise.id}
      data-status={promise.status}
      data-path={promise.fulfillment_path}
    >
      <section className="planning-drawer-section" data-testid="planning-promise-drawer-facts">
        <dl className="planning-facts">
          <Fact label={t("planning.promises.columns.product")}>
            {promiseProductText(promise.product_label, product) ?? "—"}
          </Fact>
          <Fact label={t("planning.promises.columns.qty")}>
            <BaseQty qty={promise.qty} product={product} testId="planning-promise-drawer-qty" />
          </Fact>
          <Fact label={t("planning.promises.columns.customer")}>
            {promise.customer_label ??
              (promise.customer_id !== null ? t("planning.promises.customer") : "—")}
          </Fact>
          <Fact label={t("planning.promises.columns.order")}>
            {promise.order_label ??
              (promise.sales_order_id !== null ? t("planning.promises.salesOrder") : "—")}
          </Fact>
          <Fact label={t("planning.promises.columns.path")}>
            <PromisePathChip path={promise.fulfillment_path} />
          </Fact>
          <Fact label={t("planning.promises.columns.source")}>
            {t(`planning.promises.source.${promise.source}`)}
          </Fact>
          {promise.risk_reason !== null ? (
            <Fact label={t("planning.promises.columns.risk")}>
              <span className="planning-bad" data-testid="planning-promise-drawer-risk">
                {t(`planning.promises.risk.${promise.risk_reason}`)}
              </span>
            </Fact>
          ) : null}
          {promise.withdrawn_reason_key !== null ? (
            <Fact label={t("planning.promises.status.withdrawn")}>
              <span data-testid="planning-promise-drawer-withdrawn">
                {t(`planning.promises.withdrawnReason.${promise.withdrawn_reason_key}`)}
                {promise.withdrawn_reason !== null ? `: ${promise.withdrawn_reason}` : ""}
              </span>
            </Fact>
          ) : null}
        </dl>
      </section>

      <section className="planning-drawer-section" data-testid="planning-promise-drawer-dates">
        <h3 className="planning-drawer-sub">{t("planning.promises.drawer.dates")}</h3>
        <dl className="planning-facts">
          <Fact label={t("planning.promises.columns.requested")}>
            {formatDate(promise.requested_date, lang)}
          </Fact>
          <Fact label={t("planning.promises.drawer.earliest")}>
            {formatDate(promise.earliest_date, lang)}
          </Fact>
          <Fact label={t("planning.promises.columns.promised")}>
            <strong
              data-testid="planning-promise-drawer-promised"
              data-date={promise.promised_date}
            >
              {formatDate(promise.promised_date, lang)}
            </strong>
          </Fact>
          {promise.current_projected_date !== null ? (
            <Fact label={t("planning.promises.columns.projected")}>
              <span
                className={late ? "planning-bad" : undefined}
                data-testid="planning-promise-drawer-projected"
                data-date={promise.current_projected_date}
              >
                {formatDate(promise.current_projected_date, lang)}
              </span>
            </Fact>
          ) : null}
          {promise.valid_until !== null ? (
            <Fact label={t("planning.promises.drawer.validUntil")}>
              {formatTimestamp(promise.valid_until, lang, zone)}
            </Fact>
          ) : null}
          {promise.committed_at !== null ? (
            <Fact label={t("planning.promises.drawer.committed")}>
              {formatTimestamp(promise.committed_at, lang, zone)}
            </Fact>
          ) : null}
        </dl>
      </section>

      <section className="planning-drawer-section">
        <h3 className="planning-drawer-sub">{t("planning.promises.basis.title")}</h3>
        <BasisChecklist
          basis={promise.basis}
          verdicts={promise.basis_verdicts}
          facilityId={promise.facility_id}
          timezone={zone}
          showMarks={promise.status !== "quoted"}
          horizonDays={settings.data?.schedule_horizon_days}
        />
      </section>

      {promise.verdict !== null ? <VerdictSection verdict={promise.verdict} zone={zone} /> : null}

      <section className="planning-drawer-section" data-testid="planning-promise-drawer-timeline">
        <h3 className="planning-drawer-sub">{t("planning.promises.drawer.timeline")}</h3>
        <ul className="planning-result-list">
          {promise.timeline.map((entry, i) => (
            <li
              key={`${entry.at}:${String(i)}`}
              data-testid="planning-promise-timeline-entry"
              data-status={entry.status}
            >
              <span className="planning-chips">
                <PromiseStatusChip status={entry.status} />
                <span className="planning-note">{formatTimestamp(entry.at, lang, zone)}</span>
                {entry.reason_key !== null && isWithdrawnReason(entry.reason_key) ? (
                  <span>{t(`planning.promises.withdrawnReason.${entry.reason_key}`)}</span>
                ) : null}
                {entry.reason_key !== null && isRiskReason(entry.reason_key) ? (
                  <span data-testid="planning-promise-timeline-risk" data-reason={entry.reason_key}>
                    {t(`planning.promises.risk.${entry.reason_key}`)}
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {withdrawable ? <WithdrawSection promise={promise} onPending={onPending} /> : null}
    </div>
  );
}

const WITHDRAWN_REASONS = ["manual", "re_promised", "order_cancelled", "line_cancelled"] as const;
const RISK_REASONS = [
  "stock_consumed",
  "material_delay",
  "capacity_loss",
  "schedule_slip",
] as const;
function isRiskReason(key: string): key is (typeof RISK_REASONS)[number] {
  return (RISK_REASONS as readonly string[]).includes(key);
}
function isWithdrawnReason(key: string): key is (typeof WITHDRAWN_REASONS)[number] {
  return (WITHDRAWN_REASONS as readonly string[]).includes(key);
}

/**
 * The verdict once judged (spec 03 §3.7, §4.9, D-467): kept or broken, the
 * on-time share, the shortfall and the slip, the ship date that settled it,
 * whether the break was mostly on time (`downgraded`, the facility's
 * `promise_ontime_downgrade_pct`), and the one late correction.
 */
function VerdictSection({
  verdict,
  zone,
}: {
  verdict: NonNullable<CtpPromiseDetail["verdict"]>;
  zone: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const pct = Number(verdict.pct_shipped_on_time);
  return (
    <section
      className="planning-drawer-section"
      data-testid="planning-promise-drawer-verdict"
      data-verdict={verdict.verdict}
      data-slip-days={verdict.slip_days}
      data-shortfall={verdict.shortfall_qty}
      data-downgraded={String(verdict.downgraded)}
      data-corrected={String(verdict.corrected)}
    >
      <h3 className="planning-drawer-sub">{t("planning.promises.verdict.title")}</h3>
      <p className={verdict.verdict === "kept" ? undefined : "planning-bad"}>
        {t(`planning.promises.verdict.${verdict.verdict}`)}
      </p>
      <ul className="planning-result-list">
        <li>
          {t("planning.promises.verdict.onTime", {
            qty: verdict.shipped_on_time_qty,
            judged: verdict.judged_qty,
          })}
          {Number.isFinite(pct)
            ? ` (${new Intl.NumberFormat(lang, { maximumFractionDigits: 2 }).format(pct)}%)`
            : null}
        </li>
        {Number(verdict.shortfall_qty) > 0 ? (
          <li>{t("planning.promises.verdict.shortfall", { qty: verdict.shortfall_qty })}</li>
        ) : null}
        {verdict.slip_days > 0 ? (
          <li>{t("planning.promises.verdict.slip", { count: verdict.slip_days })}</li>
        ) : null}
        {verdict.shipped_date !== null ? (
          <li data-testid="planning-promise-verdict-shipped" data-date={verdict.shipped_date}>
            {t("planning.promises.verdict.shipped", {
              date: formatDate(verdict.shipped_date, lang),
            })}
          </li>
        ) : null}
        {verdict.verdict === "broken" && verdict.downgraded ? (
          <li data-testid="planning-promise-verdict-downgraded">
            {t("planning.promises.verdict.downgraded")}
          </li>
        ) : null}
        {verdict.corrected ? (
          <li data-testid="planning-promise-verdict-corrected">
            {verdict.corrected_at !== null
              ? t("planning.promises.verdict.correctedAt", {
                  at: formatTimestamp(verdict.corrected_at, lang, zone),
                })
              : t("planning.promises.verdict.corrected")}
          </li>
        ) : null}
      </ul>
    </section>
  );
}

function WithdrawSection({
  promise,
  onPending,
}: {
  promise: CtpPromiseDetail;
  onPending: (busy: boolean) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const offline = useOffline();
  const refresh = usePromiseRefresh();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<unknown>(undefined);
  const trimmed = reason.trim();

  const send = async () => {
    setSending(true);
    onPending(true);
    setFailure(undefined);
    try {
      await withdrawPromise(promise.id, trimmed);
      setOpen(false);
      setReason("");
      await refresh(promise.facility_id, [promise.id]);
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      setFailure(error);
    } finally {
      setSending(false);
      onPending(false);
    }
  };

  return (
    <section className="planning-drawer-section" data-testid="planning-promise-drawer-withdraw">
      {offline ? <p className="planning-note">{t("planning.common.offlineWrites")}</p> : null}
      {!open ? (
        <div className="planning-actions">
          <Button
            variant="danger"
            disabled={offline}
            data-testid="planning-promise-withdraw"
            onClick={() => {
              setOpen(true);
            }}
          >
            {t("planning.promises.drawer.withdraw")}
          </Button>
        </div>
      ) : (
        <div className="planning-mps-decision">
          <p>{t("planning.promises.drawer.withdrawTitle")}</p>
          <Field label={t("planning.promises.drawer.withdrawReason")} wide>
            <textarea
              rows={3}
              maxLength={PROMISE_REASON_MAX}
              value={reason}
              data-testid="planning-promise-withdraw-reason"
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          </Field>
          {failure !== undefined ? (
            <Banner kind="bad" data-testid="planning-promise-withdraw-failed">
              {refusalText(t, failure)}
            </Banner>
          ) : null}
          <div className="planning-mps-decision-actions">
            <Button
              disabled={sending}
              onClick={() => {
                setOpen(false);
              }}
            >
              {t("planning.common.cancel")}
            </Button>
            <Button
              variant="danger"
              busy={sending}
              busyLabel={t("planning.promises.drawer.withdrawing")}
              disabled={offline || trimmed.length === 0}
              data-testid="planning-promise-withdraw-confirm"
              onClick={() => void send()}
            >
              {t("planning.promises.drawer.withdrawConfirm")}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
